import {
  Eye,
  File as FileIcon,
  FileText,
  Image as ImageIcon,
  LoaderCircle,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { fileIdentity, formatFileSize, validateFiles } from "./file-utils";

export type PersistedUploadFile = {
  id: string;
  name: string;
  size?: number;
  type?: string;
  url?: string;
  date?: string | Date;
};

type Props = {
  files: File[];
  onFilesChange: (files: File[]) => void;
  accept?: string;
  acceptLabel?: string;
  multiple?: boolean;
  maxFiles?: number;
  maxSizeBytes?: number;
  persistedFiles?: PersistedUploadFile[];
  onRemovePersisted?: (file: PersistedUploadFile) => void;
  removingPersistedId?: string | null;
  disabled?: boolean;
  uploading?: boolean;
  countPersistedTowardLimit?: boolean;
  onValidationError?: (message: string) => void;
  emptyLabel?: string;
};

const previewKind = (name: string, type?: string) => {
  const normalized = type?.toLocaleLowerCase() ?? "";
  const extension = name.split(".").pop()?.toLocaleLowerCase();
  if (normalized.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp"].includes(extension ?? "")) return "image";
  if (normalized === "application/pdf" || extension === "pdf") return "pdf";
  if (normalized.startsWith("video/") || ["mp4", "webm", "mov"].includes(extension ?? "")) return "video";
  return "file";
};

const FileGlyph = ({ name, type }: { name: string; type?: string }) => {
  const kind = previewKind(name, type);
  if (kind === "image") return <ImageIcon className="h-6 w-6" />;
  if (kind === "pdf") return <FileText className="h-6 w-6" />;
  return <FileIcon className="h-6 w-6" />;
};

type Preview = { name: string; type?: string; url: string; temporary?: boolean };

export function FileUpload({
  files,
  onFilesChange,
  accept,
  acceptLabel = "arquivos permitidos",
  multiple = true,
  maxFiles = multiple ? Number.POSITIVE_INFINITY : 1,
  maxSizeBytes,
  persistedFiles = [],
  onRemovePersisted,
  removingPersistedId,
  disabled = false,
  uploading = false,
  countPersistedTowardLimit = false,
  onValidationError,
  emptyLabel = "Nenhum arquivo cadastrado.",
}: Props) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previewTriggerRef = useRef<HTMLElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);

  const addFiles = (incoming: File[]) => {
    if (disabled || uploading) return;
    const base = multiple ? files : [];
    const result = validateFiles(base, multiple ? incoming : incoming.slice(0, 1), {
      accept,
      maxFiles,
      maxSizeBytes,
      existingCount: countPersistedTowardLimit ? persistedFiles.length : 0,
    });
    result.errors.forEach((message) => onValidationError?.(message));
    if (result.accepted.length) onFilesChange([...base, ...result.accepted]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const openPreview = (file: File | PersistedUploadFile) => {
    previewTriggerRef.current = document.activeElement as HTMLElement | null;
    const isLocal = file instanceof File;
    const url = isLocal ? URL.createObjectURL(file) : file.url;
    if (!url) {
      onValidationError?.(`Não foi possível visualizar "${isLocal ? file.name : file.name}".`);
      return;
    }
    setPreview({ name: file.name, type: file.type, url, temporary: isLocal });
  };

  const closePreview = () => {
    setPreview((current) => {
      if (current?.temporary) URL.revokeObjectURL(current.url);
      return null;
    });
    previewTriggerRef.current?.focus();
  };

  useEffect(() => {
    if (!preview) return;
    closeButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closePreview();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [preview]);

  useEffect(() => () => {
    if (preview?.temporary) URL.revokeObjectURL(preview.url);
  }, [preview]);

  const total = files.length + (countPersistedTowardLimit ? persistedFiles.length : 0);
  const allItemsEmpty = files.length === 0 && persistedFiles.length === 0;

  return (
    <div className="space-y-3">
      <div
        className={`rounded-2xl border-2 border-dashed px-5 py-7 text-center transition-colors ${dragging ? "border-blue-500 bg-blue-50" : "border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50/50"} ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
        onClick={() => !disabled && !uploading && inputRef.current?.click()}
        onDragEnter={(event) => { event.preventDefault(); if (!disabled) setDragging(true); }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setDragging(false); }}
        onDrop={(event) => { event.preventDefault(); setDragging(false); addFiles(Array.from(event.dataTransfer.files)); }}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); inputRef.current?.click(); } }}
        aria-disabled={disabled || uploading}
        aria-describedby={`${inputId}-rules`}
      >
        {uploading ? <LoaderCircle className="mx-auto h-9 w-9 animate-spin text-blue-600" /> : <UploadCloud className="mx-auto h-9 w-9 text-blue-600" />}
        <p className="mt-3 text-sm font-semibold text-slate-900">
          {uploading ? "Enviando arquivos…" : "Arraste e solte ou clique para selecionar"}
        </p>
        <p id={`${inputId}-rules`} className="mt-1 text-xs text-slate-500">
          {acceptLabel}{maxSizeBytes ? ` • até ${formatFileSize(maxSizeBytes)} por arquivo` : ""}
          {Number.isFinite(maxFiles) ? ` • ${total} de ${maxFiles}` : ""}
        </p>
        <input ref={inputRef} id={inputId} type="file" className="sr-only" accept={accept} multiple={multiple} disabled={disabled || uploading} onChange={(event) => addFiles(Array.from(event.target.files ?? []))} />
      </div>

      <div aria-live="polite" className="space-y-2">
        {files.map((file) => (
          <FileRow key={fileIdentity(file)} name={file.name} size={file.size} type={file.type} localFile={file} status={uploading ? "Enviando…" : "Selecionado"} onPreview={() => openPreview(file)} onRemove={() => onFilesChange(files.filter((item) => fileIdentity(item) !== fileIdentity(file)))} disabled={uploading} />
        ))}
        {persistedFiles.map((file) => (
          <FileRow key={file.id} name={file.name} size={file.size} type={file.type} url={file.url} date={file.date} status="Enviado" onPreview={file.url ? () => openPreview(file) : undefined} onRemove={onRemovePersisted ? () => onRemovePersisted(file) : undefined} disabled={removingPersistedId === file.id} />
        ))}
        {allItemsEmpty && <div className="rounded-xl border border-slate-200 bg-white px-4 py-5 text-center text-sm text-slate-500">{emptyLabel}</div>}
      </div>

      {preview && createPortal(
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/75 p-4" role="dialog" aria-modal="true" aria-label={`Visualização de ${preview.name}`} onMouseDown={(event) => { if (event.target === event.currentTarget) closePreview(); }}>
          <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3"><span className="truncate pr-4 text-sm font-semibold">{preview.name}</span><button ref={closeButtonRef} type="button" className="btn-icon" onClick={closePreview} aria-label="Fechar visualização"><X className="h-5 w-5" /></button></div>
            <div className="min-h-0 flex-1 overflow-auto bg-slate-100 p-3">
              {previewKind(preview.name, preview.type) === "image" && <img src={preview.url} alt={preview.name} className="mx-auto max-h-[78vh] max-w-full object-contain" />}
              {previewKind(preview.name, preview.type) === "pdf" && <iframe src={preview.url} title={preview.name} className="h-[78vh] w-full rounded-lg bg-white" />}
              {previewKind(preview.name, preview.type) === "video" && <video src={preview.url} controls className="mx-auto max-h-[78vh] max-w-full" />}
              {previewKind(preview.name, preview.type) === "file" && <div className="p-10 text-center text-sm text-slate-600">Este formato não possui pré-visualização. <a href={preview.url} target="_blank" rel="noreferrer" className="btn-link-primary">Abrir ou baixar arquivo</a></div>}
            </div>
          </div>
        </div>, document.body)}
    </div>
  );
}

function LocalImageThumbnail({ file }: { file: File }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    const nextUrl = URL.createObjectURL(file);
    setUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [file]);
  return url ? <img src={url} alt="" className="h-full w-full object-cover" /> : null;
}

function FileRow({ name, size, type, url, localFile, date, status, onPreview, onRemove, disabled }: { name: string; size?: number; type?: string; url?: string; localFile?: File; date?: string | Date; status: string; onPreview?: () => void; onRemove?: () => void; disabled?: boolean }) {
  const formattedDate = date && !Number.isNaN(new Date(date).getTime()) ? new Date(date).toLocaleDateString("pt-BR") : "";
  return <div className="flex min-w-0 flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 sm:flex-row sm:items-center">
    <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-blue-50 text-blue-600">{previewKind(name, type) === "image" && localFile ? <LocalImageThumbnail file={localFile} /> : previewKind(name, type) === "image" && url ? <img src={url} alt="" className="h-full w-full object-cover" /> : <FileGlyph name={name} type={type} />}</div>
    <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium text-slate-900" title={name}>{name}</p><p className="mt-0.5 text-xs text-slate-500">{size !== undefined ? formatFileSize(size) : "Tamanho indisponível"} • {status}{formattedDate ? ` • ${formattedDate}` : ""}</p></div>
    <div className="flex items-center gap-1 self-end sm:self-auto">
      {onPreview && <button type="button" onClick={onPreview} className="btn-icon" aria-label={`Visualizar ${name}`} title="Visualizar"><Eye className="h-4 w-4" /></button>}
      {onRemove && <button type="button" onClick={onRemove} disabled={disabled} className="btn-icon text-red-600" aria-label={`${status === "Enviado" ? "Excluir" : "Remover"} ${name}`} title={status === "Enviado" ? "Excluir" : "Remover"}>{disabled ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}</button>}
    </div>
  </div>;
}

